import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SearchIcon from '@mui/icons-material/Search';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import TuneIcon from '@mui/icons-material/Tune';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { VEHICLE_TYPES, getVehicleTypeEntry } from '../../constants/vehicleTypes';
import SearchableSelect from '../common/SearchableSelect';
import './SearchCar.style.css';

const TOP_BRANDS = [
  "Renault", "Peugeot", "Dacia", "Volkswagen",
  "Mercedes-Benz", "BMW", "Audi", "Hyundai", "Toyota", "Ford",
];

const PRICES = [
  { label: "50 000 DH",     value: 50000   },
  { label: "100 000 DH",    value: 100000  },
  { label: "150 000 DH",    value: 150000  },
  { label: "200 000 DH",    value: 200000  },
  { label: "300 000 DH",    value: 300000  },
  { label: "500 000 DH",    value: 500000  },
  { label: "700 000 DH",    value: 700000  },
  { label: "1 000 000 DH",  value: 1000000 },
];

export const SearchCar = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [brands,      setBrands]      = useState([]);
  const [models,      setModels]      = useState([]);
  const [generations, setGenerations] = useState([]);
  const [years,       setYears]       = useState([]);

  const [brandId,      setBrandId]      = useState('');
  const [modelId,      setModelId]      = useState('');
  const [generationId, setGenerationId] = useState('');
  const [year,     setYear]     = useState('');
  const [maxPrice, setMaxPrice] = useState('');

  const [count,    setCount]    = useState(null);
  const [counting, setCounting] = useState(false);
  const [vehicleType, setVehicleType] = useState('cars');
  const apiSegment = getVehicleTypeEntry(vehicleType).apiSegment || 'Cars';

  const generationSelectRef = useRef(null);
  const yearSelectRef       = useRef(null);
  const priceSelectRef      = useRef(null);

  // <label> already forwards clicks to its <select> natively, but only reliably
  // when the click lands on a child node — clicking the label's own empty
  // padding (above/below the visible text) doesn't always forward in every
  // browser. This catches exactly that case (click landed on the label itself,
  // not a descendant) without double-opening clicks that already worked.
  const openSelect = (ref) => (e) => {
    if (e.target === e.currentTarget && ref.current && !ref.current.disabled) ref.current.showPicker?.();
  };

  /* ── Sync URL ─────────────────────────────── */
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    setBrandId(p.get('brandId')           || '');
    setModelId(p.get('modelId')           || '');
    setGenerationId(p.get('generationId') || '');
    setYear(p.get('year')         || '');
    setMaxPrice(p.get('maxPrice') || '');
    setVehicleType(p.get('vehicleType')   || 'cars');
  }, [location.search]);

  /* ── Brands (scoped to the selected vehicle type's own controller) ──────── */
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/${apiSegment}/brands`)
      .then(r => {
        if (Array.isArray(r.data)) {
          setBrands([...r.data].sort((a, b) => {
            const aT = TOP_BRANDS.includes(a.makeName);
            const bT = TOP_BRANDS.includes(b.makeName);
            if (aT && !bT) return -1;
            if (!aT && bT) return 1;
            return a.makeName.localeCompare(b.makeName);
          }));
        }
      })
      .catch(() => {});
  }, [apiSegment]);

  /* ── Models ───────────────────────────────── */
  useEffect(() => {
    if (!brandId) { setModels([]); setModelId(''); return; }
    axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/${apiSegment}/models/${brandId}`)
      .then(r => { if (Array.isArray(r.data)) setModels(r.data); })
      .catch(() => {});
  }, [brandId, apiSegment]);

  /* ── Generations (Voitures only — Motos/Utilitaires have no generations) ── */
  useEffect(() => {
    if (!modelId || vehicleType !== 'cars') { setGenerations([]); setGenerationId(''); return; }
    axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/Cars/generations/${modelId}`)
      .then(r => { if (Array.isArray(r.data)) setGenerations(r.data); })
      .catch(() => {});
  }, [modelId, vehicleType]);

  /* ── Years ────────────────────────────────── */
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/cars/years`)
      .then(r => { if (Array.isArray(r.data)) setYears([...r.data].reverse()); })
      .catch(() => {});
  }, []);

  /* ── Live count ───────────────────────────── */
  useEffect(() => {
    const ctrl = new AbortController();
    setCounting(true);
    axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/${apiSegment}/count`, {
      params: { brandId: brandId || null, modelId: modelId || null, generationId: generationId || null, year: year || null, maxPrice: maxPrice || null },
      signal: ctrl.signal,
    })
      .then(r => setCount(r.data.count))
      .catch(() => {})
      .finally(() => setCounting(false));
    return () => ctrl.abort();
  }, [apiSegment, brandId, modelId, generationId, year, maxPrice]);

  const handleVehicleTypeChange = (key) => {
    setVehicleType(key);
    setBrandId(''); setModelId(''); setGenerationId('');
  };

  const handleSearch = () => {
    const p = new URLSearchParams();
    if (vehicleType !== 'cars') p.append('vehicleType', vehicleType);
    if (brandId)      p.append('brandId',      brandId);
    if (modelId)      p.append('modelId',      modelId);
    if (generationId) p.append('generationId', generationId);
    if (year)     p.append('year',     year);
    if (maxPrice) p.append('maxPrice', maxPrice);
    navigate(`/featured-cars?${p.toString()}`);
  };

  const topBrands  = brands.filter(b =>  TOP_BRANDS.includes(b.makeName));
  const restBrands = brands.filter(b => !TOP_BRANDS.includes(b.makeName));

  const btnLabel = counting
    ? t('loading')
    : count !== null
      ? (count <= 1 ? t('seeResults', { count }) : t('seeResultsPlural', { count }))
      : t('searchBtn');

  return (
    <div className="sc-wrap">

      {/* ── Headline ──────────────────────────── */}
      <div className="sc-headline">
        <h1 className="sc-headline__title">{t('heroTitle')}</h1>
        <p className="sc-headline__sub">{t('heroSub')}</p>
      </div>

      {/* ── Vehicle type tabs ─────────────────── */}
      <div className="sc-vtypes">
        {VEHICLE_TYPES.map(({ key, i18n, icon: Icon, enabled }) => (
          <button
            key={key}
            type="button"
            className={`sc-vtype${vehicleType === key ? ' sc-vtype--active' : ''}${!enabled ? ' sc-vtype--disabled' : ''}`}
            disabled={!enabled}
            title={enabled ? undefined : t('vehicleTypes.comingSoon')}
            onClick={() => enabled && handleVehicleTypeChange(key)}
          >
            <Icon sx={{ fontSize: 19 }} />
            <span>{t(i18n)}</span>
          </button>
        ))}
      </div>

      {/* ── Search bar ────────────────────────── */}
      <div className="sc-bar">

        {/* Brand */}
        <SearchableSelect
          variant="segment"
          className="sc-segment"
          icon={<DirectionsCarIcon sx={{ fontSize: 17 }} />}
          label={t('brand')}
          placeholder={t('allBrands')}
          value={brandId}
          onChange={v => { setBrandId(v); setModelId(''); setGenerationId(''); }}
          groups={[
            ...(topBrands.length  > 0 ? [{ label: t('popularBrands'), options: topBrands.map(b  => ({ value: b.makeId, label: b.makeName })) }] : []),
            ...(restBrands.length > 0 ? [{ label: t('otherBrands'),   options: restBrands.map(b => ({ value: b.makeId, label: b.makeName })) }] : []),
          ]}
        />

        <div className="sc-divider" />

        {/* Model */}
        <SearchableSelect
          variant="segment"
          className={`sc-segment${!brandId ? ' sc-segment--disabled' : ''}`}
          icon={<TuneIcon sx={{ fontSize: 17 }} />}
          label={t('model')}
          placeholder={t('allModels')}
          value={modelId}
          onChange={v => { setModelId(v); setGenerationId(''); }}
          disabled={!brandId}
          options={models.map(m => ({ value: m.modelId ?? m.model_id, label: m.model_Name ?? m.model_name }))}
        />

        {generations.length > 0 && (
          <>
            <div className="sc-divider" />

            {/* Generation */}
            <label className="sc-segment" onClick={openSelect(generationSelectRef)}>
              <span className="sc-segment__icon"><TuneIcon sx={{ fontSize: 17 }} /></span>
              <div className="sc-segment__body">
                <span className="sc-segment__label">{t('generation')}</span>
                <select
                  ref={generationSelectRef}
                  className="sc-segment__select"
                  value={generationId}
                  onChange={e => setGenerationId(e.target.value)}
                >
                  <option value="">{t('allGenerations')}</option>
                  {generations.map(g => (
                    <option key={g.generationId} value={g.generationId}>{g.generation_Name}</option>
                  ))}
                </select>
              </div>
            </label>
          </>
        )}

        <div className="sc-divider" />

        {/* Year */}
        <label className="sc-segment" onClick={openSelect(yearSelectRef)}>
          <span className="sc-segment__icon"><CalendarTodayIcon sx={{ fontSize: 17 }} /></span>
          <div className="sc-segment__body">
            <span className="sc-segment__label">{t('year')}</span>
            <select
              ref={yearSelectRef}
              className="sc-segment__select"
              value={year}
              onChange={e => setYear(e.target.value)}
            >
              <option value="">{t('firstRegistration')}</option>
              {years.map(y => (
                <option key={y.yearId ?? y.year_val} value={y.year_val}>
                  {y.year_val}
                </option>
              ))}
            </select>
          </div>
        </label>

        <div className="sc-divider" />

        {/* Price */}
        <label className="sc-segment" onClick={openSelect(priceSelectRef)}>
          <span className="sc-segment__icon"><LocalOfferIcon sx={{ fontSize: 17 }} /></span>
          <div className="sc-segment__body">
            <span className="sc-segment__label">{t('maxPrice')}</span>
            <select
              ref={priceSelectRef}
              className="sc-segment__select"
              value={maxPrice}
              onChange={e => setMaxPrice(e.target.value)}
            >
              <option value="">{t('selectMaxPrice')}</option>
              {PRICES.map(p => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </div>
        </label>

        {/* Button */}
        <button
          className="sc-btn"
          onClick={handleSearch}
          disabled={counting || count === 0}
        >
          <SearchIcon sx={{ fontSize: 20 }} />
          <span className="sc-btn__label">{btnLabel}</span>
        </button>

      </div>
    </div>
  );
};
